import axios, { type AxiosResponse } from 'axios'
import { getContext } from './general'
import { getValueAtPath } from './util'
import { addFilterEngineParam } from './filterEngine'
import API_ENDPOINTS, {
  type ApiEndpoint,
  type EndpointKey,
} from '@/api/api.config'

const cachedRequests: Record<string, number> = {}
// Tracks the current "owner" of each in-flight GET request by cache key, so a
// deliberately-superseding request (one that aborted the previous owner) can take
// over the slot immediately instead of waiting for the old owner's async cleanup —
// see the ownership-token handling below.
interface InFlightEntry {
  signal?: AbortSignal
  token: symbol
}
const inFlightRequests: Record<string, InFlightEntry> = {}
// A loadingKey is one shared UI flag per endpoint, not per specific request --
// two requests with different params (e.g. different filters) have different
// fullCacheKey values but the SAME loadingKey. Reference-counted per loadingKey
// so it's only cleared once nothing wants it true anymore, regardless of
// whether the concurrent requests share params or not.
const loadingKeyRefCounts: Record<string, number> = {}
const DEFAULT_CACHE_TIME = 120 // 2 minutes
const GENERIC_ERROR_FALLBACK
  = 'Encountered an error while communicating with the server.'

function normalizeResponseData<TRes>(
  endpointKey: EndpointKey,
  data: TRes,
): TRes {
  if (
    endpointKey === 'fetchAudience'
    && data
    && !Array.isArray(data)
    && Array.isArray((data as { rows?: unknown[] }).rows)
  ) {
    return (data as unknown as { rows: TRes }).rows
  }

  return data
}

export async function requestApi<TReq, TRes>(
  endpointKey: EndpointKey,
  payload?: any,
): Promise<TRes | undefined> {
  // FIXME André: get specific types for endpointKey
  const endpoint = API_ENDPOINTS[endpointKey] as ApiEndpoint<any, TRes>

  // Initialize helper variables
  const {
    method = 'GET',
    url,
    storeKey,
    cache,
    cacheTtl = DEFAULT_CACHE_TIME,
    loadingKey,
    resetCache,
    mutations = [],
    errorMessage,
    successMessage,
    successMessageTitle,
  } = endpoint
  const { $axios, $store, $arNotification } = getContext()
  const promoterOid
    = $store?._modules?.root?.state?.authModule?.account?.promoterOid
  const methodValue = method.toUpperCase()
  const baseParams = { promoterOid, ...(payload?.params || {}) }
  const params = addFilterEngineParam(
    typeof url === 'function' ? url(baseParams as any) : url,
    methodValue,
    baseParams,
  )
  const urlValue = (typeof url === 'function' ? url(params) : url).replace(
    /:([^/]+)/g,
    (_, key) => String(params[key]),
  )
  let fullCacheKey = ''
  let ownershipToken: symbol | undefined

  // Ignore if it's a GET request that is cached (with non-expired TTL) or ongoing
  if (methodValue === 'GET') {
    fullCacheKey
      = endpointKey + '::' + $axios.getUri({ url: urlValue, params: params })
    const existingOwner = inFlightRequests[fullCacheKey]
    // An existing owner whose own signal is already aborted is being torn down by
    // its caller (e.g. a newer identical request superseding it) — don't treat it
    // as "still ongoing", or the replacement gets silently dropped here while the
    // caller's UI (already cleared in anticipation of fresh data) never refills.
    const existingOwnerIsStale = existingOwner?.signal?.aborted === true
    if (existingOwner && !existingOwnerIsStale) {
      console.log('Similar request already ongoing -> Ignoring')
      return
    }
    if (cache) {
      const cachedData = getValueAtPath($store.state, storeKey)
      if (
        (cachedData || (!storeKey && cache))
        && Date.now() - cachedRequests[fullCacheKey] < cacheTtl * 1000
      ) {
        console.log('Similar request already cached -> Using cache')
        return cachedData
      }
    }
    ownershipToken = Symbol('requestApi-in-flight-owner')
    inFlightRequests[fullCacheKey] = { signal: payload?.signal, token: ownershipToken }
  }

  // Set loading state
  if (loadingKey && $store) {
    loadingKeyRefCounts[loadingKey] = (loadingKeyRefCounts[loadingKey] || 0) + 1
    $store.commit(
      'SET_LOADING_STATE',
      { key: loadingKey, value: true },
      { root: true },
    )
  }

  // Send request
  return $axios
    .request<any, AxiosResponse<TRes>>({
      url: urlValue,
      method: methodValue,
      params: params,
      ...(methodValue !== 'GET' ? { data: payload?.body || {} } : {}),
      ...(payload?.signal ? { signal: payload.signal } : {}),
    })
    .then((response) => {
      const responseData = normalizeResponseData(endpointKey, response.data)

      // Store cache
      if (storeKey) {
        if (payload?.params?.$skip > 0 && !payload?.skipCacheAppend) {
          // handle infinite scroll by appending results at the end when requests contains $skip
          $store.commit(
            'CONCAT_CACHE',
            { key: storeKey, value: responseData },
            { root: true },
          )
        }
        else {
          $store.commit(
            'SET_CACHE',
            { key: storeKey, value: responseData },
            { root: true },
          )
          cachedRequests[fullCacheKey] = Date.now()
        }
      }

      // trigger reset cache mutations
      resetCache?.forEach((keyToReset) => {
        Object.keys(cachedRequests).forEach((cacheKey) => {
          if (cacheKey.startsWith(keyToReset + '::')) {
            delete cachedRequests[cacheKey]
          }
        })
      })

      // trigger associated store mutations
      mutations.forEach((mutationName) => {
        $store.commit(mutationName, responseData)
      })

      // Success message
      if (successMessage && !payload?.suppressSuccess) {
        const successMessageValue
          = typeof successMessage === 'function'
            ? successMessage(payload, response as any)
            : successMessage
        const successMessageTitleValue = successMessageTitle
          ? typeof successMessageTitle === 'function'
            ? successMessageTitle(payload, response as any)
            : successMessageTitle
          : undefined
        $arNotification.push({
          type: 'success',
          message: successMessageValue,
          title: successMessageTitleValue,
        })
      }

      return responseData
    })
    .catch((error) => {
      // A deliberately superseded/cancelled request (e.g. a newer search replacing
      // an in-flight one) is not a failure — don't surface an error notification.
      if (axios.isCancel(error)) {
        throw error
      }

      // Error Handling (defined error, server error, generic error)
      console.error(`API REQUEST FAILED FOR ENDPOINT ${endpointKey}}:`, error)
      const errorMessageValue
        = typeof errorMessage === 'function'
          ? errorMessage(payload, error)
          : errorMessage
      $arNotification.push({
        type: 'error',
        message: errorMessageValue || error?.message || GENERIC_ERROR_FALLBACK,
      })
      throw error
    })
    .finally(() => {
      // Decrement first, then only clear the flag once nothing else wants it
      // true — a request for a DIFFERENT filter/params (different fullCacheKey,
      // same loadingKey) can still be genuinely running, and an ownership check
      // scoped to fullCacheKey alone can't see that.
      if (loadingKey) {
        loadingKeyRefCounts[loadingKey] = Math.max(0, (loadingKeyRefCounts[loadingKey] || 0) - 1)
        if (loadingKeyRefCounts[loadingKey] === 0) {
          $store.commit(
            'SET_LOADING_STATE',
            { key: loadingKey, value: false },
            { root: true },
          )
        }
      }
      // Only clear the slot if we still own it — a superseding request may already
      // have taken over (see existingOwnerIsStale above), and its own `.finally()`
      // is what should clear it, not this now-stale one.
      if (
        fullCacheKey
        && ownershipToken
        && inFlightRequests[fullCacheKey]?.token === ownershipToken
      ) {
        delete inFlightRequests[fullCacheKey]
      }
    })
}
